/* eslint-disable @typescript-eslint/no-explicit-any */

import { FC, useEffect, useState } from 'react';
import { Container, ExportTableButton } from '@/components';
import { useMutation, useQuery } from '@tanstack/react-query';
import { TableData } from '@/components/Table/types';
import { useAuthContext, useToastNotification } from '@/hooks';
import { useDownloadPdf } from '@/helpers/downloadPdf';

import { faPen, faPrint } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { getContainerReleaseList } from './getContainerReleaseList';
import ContainerRealase from './components/ContainerReleaseTable/ContainerReleaseTable';
import styles from './ContainerRelease.module.scss';
import { ContainerRelease as IContainerRelease } from './types';
import { columnsToExport } from './columnsToExport';
import EditSelectedModal from './EditSelectedModal/EditSelectedModal';
import { getPrintRealease } from './getPrintRealease';

const FILE_NAME = 'ContainerReleaseList-Montecon';

export type ParsedIContainerRelease = Omit<
  IContainerRelease,
  'tipoContenedor' & 'cierreStacking'
> & {
  tipo?: string;
  tamano?: string;
  cierreStacking?: Date;
};

const ContainerRelease: FC = () => {
  const { t } = useTranslation('containerRelease');
  const [parsedData, setParsedData] = useState<ParsedIContainerRelease[]>([]);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);

  const { showToast } = useToastNotification();
  const { downloadPdf } = useDownloadPdf();
  const { userDataDecoded } = useAuthContext();
  const { mutate: printRelease } = useMutation({
    mutationKey: ['printRelease'],
    mutationFn: () =>
      getPrintRealease(
        selectedItems.map((val) => val.oidImport),
        userDataDecoded?.username
      ),
    onSuccess: (response: any) => {
      downloadPdf(
        response,
        `liberacionContenedor/es ${selectedItems.map((val) => val.oidImport)}`
      );
      setIsDownloading(false);
    },
    onError: () => {
      setIsDownloading(false);
    },
  });

  const handlePrint = () => {
    if (!selectedItems.length) {
      showToast({
        severity: 'error',
        detail: t('noSelectedItems'),
      });
    }
    if (selectedItems.length) {
      setIsDownloading(true);

      printRelease();
    }
  };

  const handleSelectionChange = (e: { value: any }) => {
    setSelectedItems(e.value);
  };

  const { data, isLoading } = useQuery<IContainerRelease[]>({
    queryKey: ['containerRelease'],
    queryFn: getContainerReleaseList,
  });

  const handleSuccess = async () => {
    const newData = await getContainerReleaseList();
    setParsedData(newData);
  };

  useEffect(() => {
    if (data) {
      const newData = data.map((item: any) => {
        const { ...rest } = item;
        const llegada = item?.llegada ? new Date(item?.llegada) : null;
        const fechaRecibirHasta = item?.fechaRecibirHasta
          ? new Date(item?.fechaRecibirHasta)
          : null;
        const fechaLiberadoHasta = item?.fechaLiberadoHasta
          ? new Date(item?.fechaLiberadoHasta)
          : null;

        return {
          ...rest,
          llegada,
          fechaRecibirHasta,
          fechaLiberadoHasta,
        };
      });
      setParsedData(newData);
    }
  }, [data]);
  const isTableHidden = !parsedData && !isLoading;

  return (
    <Container className={styles.solas}>
      <header>
        <h2 className={styles.title}>{t('title')}</h2>
        {!isTableHidden && (
          <ExportTableButton
            data={data as TableData}
            isDisabled={isLoading}
            columnsToExport={columnsToExport}
            fileName={FILE_NAME}
          />
        )}
      </header>
      <div className={styles.secondaryActionsWrapper}>
        <div>
          <EditSelectedModal
            onSuccess={handleSuccess}
            selectedItems={selectedItems}
            totalContainers={parsedData.length}
            toggleComponent={
              <button
                disabled={!selectedItems.length}
                type="button"
                className={styles.secondaryActionsButtons}
              >
                <FontAwesomeIcon icon={faPen} />
                {t('editSelected')}
              </button>
            }
            disabled={!selectedItems.length}
          />

          <button
            type="button"
            className={styles.secondaryActionsButtons}
            onClick={handlePrint}
            disabled={selectedItems.length > 30 || isDownloading}
          >
            <FontAwesomeIcon icon={faPrint} />
            {t('printReceipt')}
          </button>
        </div>
        <p className={styles.containersSelected}>
          {selectedItems?.length
            ? `${selectedItems?.length} ${t('containersSelected')}`
            : t('noContainerSelected')}
        </p>
      </div>
      <ContainerRealase
        data={parsedData}
        isLoading={isLoading}
        selectedItems={selectedItems}
        handleSelectionChange={handleSelectionChange}
      />
    </Container>
  );
};

export default ContainerRelease;
